import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs/operators';
import { ApiError, HttpService } from '../../../services/http.service';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';
import { AddSchoolComponent } from '../add-school/add-school.component';

interface TeacherDialogData {
    initialData?: APIResponse;
    EditMode: boolean;
    schoolID: string;
}

interface APIResponse {
    phoneNumber: string;
    schoolId: string;
    schoolName: string;
    address: string;
    suburb: string;
    postcode: string;
    teacherId: string;
    studentId: string;
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    isActive: boolean | string;
    dateOfBirth: string;
    assignedTeacher: string;
    name: string;
    id: string;
    school: any;
}

interface ApiDataResponse {
    status: boolean;
    message: string;
    data: APIResponse | APIResponse[];
}

@Component({
    selector: 'add-teacher',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        InputComponent,
        MatDialogModule,
    ],
    templateUrl: './add-teacher.component.html',
    styleUrls: ['./add-teacher.component.scss']
})
export class AddTeacherComponent implements OnInit, OnDestroy {
    @Output() formSubmitted = new EventEmitter<any>();

    form: FormGroup;
    schoolOptions: any[] = [];
    schoolOptionsFull: any[] = [];

    isProcessing = false;
    isEditMode: boolean = false;
    selectedSchoolID: any = "";
    loading = false;

    customErrorMessages = {
        firstName: { required: 'Please enter a first name.' },
        lastName: { required: 'Please enter a last name.' },
        email: {
            required: 'Please enter a email address.',
            email: 'Please enter a valid email address.'
        },
        phoneNumber: {
            required: 'Please enter a phone number.',
            pattern: 'Please enter a valid phone number.'
        }
        ,
        school: { required: 'Please select a school.' }
    };

    constructor(
        private fb: FormBuilder,
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<AddTeacherComponent>,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: TeacherDialogData
    ) {
        this.form = this.fb.group({
            school: ['', requiredTrimmed],
            firstName: ['', requiredTrimmed],
            lastName: ['', requiredTrimmed],
            email: ['', [Validators.required, Validators.pattern(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
            )]],
            phoneNumber: [
                '',
                [
                    // Validators.pattern(/^(?:\+?61|0)[\s]?[23478](?:\s?\d){8}$/)
                    Validators.pattern(/^(?:0[47]\d{8}|\+61 ?[47]\d{8})$/)
                ]
            ]


        });
        this.isEditMode = data?.EditMode || false;
        this.selectedSchoolID = data?.schoolID || "";
    }

    ngOnInit(): void {
        this.fetchSchoolsDropDown().subscribe({
            next: () => {
                if (this.isEditMode && this.data?.initialData) {
                    setTimeout(() => {
                        this.patchFormForEdit(this.data.initialData!);
                    }, 100);
                }
                if (this.selectedSchoolID) {
                    setTimeout(() => {
                        this.patchSchoolFromSelectedID();
                    }, 100);
                }
            },
            complete: () => {
                if (this.selectedSchoolID && !this.form.get('school')?.value) {
                    setTimeout(() => {
                        this.patchSchoolFromSelectedID();
                    }, 100);
                }
            }
        });
    }

    ngOnDestroy(): void { }

    fetchSchoolsDropDown() {
        this.loading = true;
        const type = 2;
        const currentSelectedValue = this.form.get('school')?.value;
        return this.httpService.get<ApiDataResponse>(`Schools/dropdown?status=${type}`).pipe(
            tap({
                next: (response) => {
                    if (Array.isArray(response.data)) {
                        const activeSchools = response.data.filter(
                            (item: any) => item.status === 'Active'
                        );

                        const newSchoolOptions = activeSchools.map(item => ({
                            label: item.name,
                            value: item.id
                        }));

                        const newSchoolOptionsFull = Array.isArray(response.data)
                            ? response.data.map(item => ({ label: item.name, value: item.id }))
                            : [];

                        if (this.isEditMode && this.data.initialData) {
                            const exists = this.data.initialData
                                ? newSchoolOptions.find(
                                    (option: { value: any }) => option.value === this.data.initialData!.schoolId
                                )
                                : undefined;

                            if (!exists && this.data.initialData.schoolId) {
                                newSchoolOptions.unshift({
                                    label: this.data.initialData.school || this.data.initialData.schoolName,
                                    value: this.data.initialData.schoolId
                                });
                            }
                        }

                        if (currentSelectedValue) {
                            const selectedExists = newSchoolOptions.some(opt => opt.value === currentSelectedValue);
                            if (!selectedExists) {
                                const missingSchool = newSchoolOptionsFull.find(opt => opt.value === currentSelectedValue);
                                if (missingSchool) {
                                    newSchoolOptions.unshift(missingSchool);
                                }
                            }
                        }

                        this.schoolOptions = newSchoolOptions;
                        this.schoolOptionsFull = newSchoolOptionsFull;
                        if (currentSelectedValue) {
                            setTimeout(() => {
                                const schoolControl = this.form.get('school');
                                if (schoolControl && !schoolControl.value) {
                                    schoolControl.setValue(currentSelectedValue, { emitEvent: true });
                                }
                            });
                        }
                    } else {
                        this.showErrorToast('Something went wrong. Please try again later.');
                    }
                },
                error: (err) => {
                    this.schoolOptions = [];
                    this.showErrorToast(err?.message || 'Something went wrong. Please try again later.');
                }
            }),
            finalize(() => {
                this.loading = false;
                this.cdr.detectChanges();
            })
        );
    }


    private patchSchoolFromSelectedID(): void {
        if (this.selectedSchoolID) {
            const selectedSchool = this.schoolOptionsFull.find(opt => opt.value === this.selectedSchoolID);
            if (selectedSchool) {
                const schoolControl = this.form.get('school');
                if (schoolControl) {
                    schoolControl.setValue(selectedSchool.value, { emitEvent: true });
                    const existsInOptions = this.schoolOptions.some(opt => opt.value === selectedSchool.value);
                    if (!existsInOptions) {
                        this.schoolOptions = [selectedSchool, ...this.schoolOptions];
                    }

                    this.cdr.detectChanges();
                }
            }
        }
    }

    private patchFormForEdit(teacherData: APIResponse): void {

        this.patchBasicFields(teacherData);
        const schoolId = teacherData.schoolId
        const schoolName = teacherData.schoolName || teacherData.school;

        if (schoolId) {
            let selectedSchoolOption = this.schoolOptionsFull.find(
                option => option.value === schoolId
            );

            if (!selectedSchoolOption && schoolName) {
                selectedSchoolOption = this.schoolOptionsFull.find(
                    option => option.label.toLowerCase() === schoolName.toLowerCase()
                );
            }

            if (!selectedSchoolOption && schoolId && schoolName) {
                const newOption = { label: schoolName, value: schoolId };
                this.schoolOptions.unshift(newOption);
                this.schoolOptionsFull.unshift(newOption);
                selectedSchoolOption = newOption;
            }

            if (selectedSchoolOption?.value) {
                this.form.get('school')?.setValue(selectedSchoolOption.value, { emitEvent: true });
            } else {
                this.form.get('school')?.markAsTouched();
            }
        }

        Object.keys(this.form.controls).forEach(key => {
            this.form.get(key)?.markAsTouched();
        });
        this.cdr.detectChanges();
    }

    private patchBasicFields(teacherData: APIResponse): void {
        const rawPhone = (teacherData as any)['phone number'] ?? (teacherData as any).phoneNumber ?? teacherData.mobileNumber;
        const formValues = {
            firstName: (teacherData as any)['first name'] ?? (teacherData as any).firstName ?? teacherData.firstName,
            lastName: (teacherData as any)['last name'] ?? (teacherData as any).lastName ?? teacherData.lastName,
            email: teacherData.email || '',
            // The table renders '-' as a placeholder for a missing phone number; treat it as empty
            // so the optional phone field does not fail its pattern validation on edit.
            phoneNumber: this.stripPlaceholder(rawPhone),
        };
        this.form.patchValue(formValues);
    }

    private stripPlaceholder(value: any): string {
        const trimmed = (value ?? '').toString().trim();
        return trimmed === '-' ? '' : trimmed;
    }

    saveTeacher(formData: any): void {
        if (this.isProcessing) return;
        this.isProcessing = true;

        const payload = {
            schoolId: formData.school || this.selectedSchoolID,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            mobileNumber: formData.phoneNumber
        };

        const apiCall = this.isEditMode
            ? this.httpService.put<ApiDataResponse>(`teachers/${this.data.initialData?.teacherId ? this.data.initialData?.teacherId : this.data.initialData?.id}`, payload)
            : this.httpService.post<ApiDataResponse>('teachers', payload);

        apiCall.pipe(
            finalize(() => {
                this.isProcessing = false;
                this.form.enable();
            })
        ).subscribe({
            next: (response) => {
                if (response.status) {
                    this.showSuccessToast(response.message);
                    this.formSubmitted.emit(formData);
                    this.dialogRef.close({ success: true, data: response.data });
                }
            },
            error: (err: ApiError) => {
                if (err.errors) {
                    console.log(err.errors);
                    Object.values(err.errors).forEach((item: any) => {
                        const msg = Array.isArray(item)
                            ? item[0]
                            : item || "Something went wrong. Please try again later.";

                        this.showErrorToast(msg);
                    });
                } else {
                    this.showErrorToast(err?.message || 'Something went wrong. Please try again later.');
                }
            }
        });
    }

    onSubmit(): void {
        if (this.form.valid) {
            this.saveTeacher(this.form.value);
        } else {
            this.form.markAllAsTouched();
            this.scrollToFirstInvalidControl();
        }
    }

    onAddNewSchool(): void {
        if (this.isProcessing) return;
        const currentSelectedSchool = this.form.get('school')?.value;

        const dialogRef = this.dialog.open(AddSchoolComponent, {
            width: '50.5rem',
            maxWidth: '90vw',
            minWidth: '300px',
            data: { initialData: undefined, EditMode: false },
            disableClose: true,
            autoFocus: false,
            panelClass: 'custom-dialog-container'
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.success) {
                this.fetchSchoolsDropDown().subscribe(() => {
                    setTimeout(() => this.autoSelectNewSchool(result.data), 150);
                });
            } else {
                if (currentSelectedSchool) {
                    const schoolExists = this.schoolOptions.some(opt => opt.value === currentSelectedSchool);
                    if (schoolExists) {
                        this.form.get('school')?.setValue(currentSelectedSchool, { emitEvent: true });
                    } else {
                        const fullSchool = this.schoolOptionsFull.find(opt => opt.value === currentSelectedSchool);
                        if (fullSchool) {
                            this.schoolOptions = [fullSchool, ...this.schoolOptions];
                            this.form.get('school')?.setValue(currentSelectedSchool, { emitEvent: true });
                        }
                    }
                }
                this.cdr.detectChanges();
            }
        });
    }

    private autoSelectNewSchool(data: any): void {
        const schoolId = data.id || data.schoolId;
        if (schoolId && this.form.get('school')) {
            const schoolExists = this.schoolOptions.some(opt => opt.value === schoolId);
            if (!schoolExists) {
                const newSchoolOption = {
                    label: data.name || data.schoolName,
                    value: schoolId
                };
                this.schoolOptions = [newSchoolOption, ...this.schoolOptions];
            }

            this.form.get('school')?.setValue(schoolId, { emitEvent: true });
            this.showSuccessToast('School added and selected successfully');
            this.cdr.detectChanges();
        }
    }

    onCancel(): void {
        if (this.isProcessing) return;
        this.dialogRef.close(false);
    }

    private scrollToFirstInvalidControl(): void {
        setTimeout(() => {
            const firstInvalidControl = document.querySelector('.ng-invalid:not(form)');
            if (firstInvalidControl) {
                firstInvalidControl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
                (firstInvalidControl as HTMLElement).focus();
            }
        }, 100);
    }

    private showToast(message: string, type: 'success' | 'error'): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', type === 'success' ? 'success-toast' : 'error-toast']
        });
    }

    private showSuccessToast(message: string): void {
        this.showToast(message, 'success');
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.showToast(message, 'error');
    }
}